import React, { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Landmark, Copy, Globe2, ArrowDownToLine, FlaskConical, FileDown, Mail } from "lucide-react";

const Detail = ({ label, value }) => {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between gap-3 text-xs py-1">
      <span className="text-muted-foreground">{label}</span>
      <button className="numeric font-medium hover:text-primary transition-colors"
        onClick={() => { navigator.clipboard?.writeText(String(value)); toast.success("Copied"); }}
        title="Copy">
        {value} <Copy className="inline h-3 w-3 opacity-50" />
      </button>
    </div>
  );
};

/** Local collection accounts across Airwallex, Currencycloud and Nium. */
export const CollectionAccounts = ({ onCredited }) => {
  const [info, setInfo] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [payments, setPayments] = useState([]);
  const [ccy, setCcy] = useState("");
  const [provider, setProvider] = useState("auto");
  const [amount, setAmount] = useState("2500");
  const [busy, setBusy] = useState(false);
  const [mail, setMail] = useState(null);

  const load = useCallback(async () => {
    const [p, c, a, pay, rc] = await Promise.all([
      api.get("/collect/providers"), api.get("/collect/currencies"),
      api.get("/collect/accounts"), api.get("/collect/payments"),
      api.get("/deposit/receipts"),
    ]);
    setInfo(p.data); setCurrencies(c.data); setAccounts(a.data); setPayments(pay.data);
    setMail(rc.data);
  }, []);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/collect/accounts", {
        currency: ccy, provider: provider === "auto" ? undefined : provider });
      toast.success(`${data.currency} collection account ready`,
        { description: `${data.provider_label} · ${data.rail}` });
      setCcy(""); load();
    } catch (e) { toast.error("Couldn't open that account", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  const simulate = async (acct) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/collect/accounts/${acct.id}/simulate`,
        { amount: Number(amount), sender: "Sandbox payer" });
      window.dispatchEvent(new Event("tharwy:deposit"));
      toast.success("Payment credited",
        { description: `${data.credited_amount.toLocaleString()} ${data.credited_currency} added to your wallet` });
      load(); onCredited?.();
    } catch (e) { toast.error("Simulation failed", { description: e?.response?.data?.detail }); }
    finally { setBusy(false); }
  };

  if (!info) return null;
  const opened = new Set(accounts.map((a) => `${a.currency}:${a.provider}`));
  const options = currencies.filter((c) =>
    provider === "auto" ? true : c.providers.includes(provider));

  return (
    <Card className="p-5 shadow-premium mt-4 sm:mt-5" data-testid="collection-accounts-card">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <div className="flex items-center gap-2 text-sm font-medium">
            <Globe2 className="h-4 w-4" />Collect locally in {info.currency_count} currencies
          </div>
          <p className="text-[11px] text-muted-foreground mt-1 max-w-xl leading-relaxed">
            Named account details on {info.local_rail_count} local rails — SEPA, Faster Payments,
            ACH, SPEI, UPI, PIX and more. Money paid in lands in your wallet in the same currency
            when we hold it, otherwise it converts to USD at {info.fx_spread_pct}% spread.
          </p>
        </div>
        <div className="flex flex-wrap gap-1.5" data-testid="collection-providers">
          {info.providers.map((p) => (
            <Badge key={p.name} variant="secondary" className="text-[10px] gap-1"
              data-testid={`collection-provider-${p.name}`}>
              {p.label} · {p.currency_count}
              <span className={p.configured ? "text-emerald-600" : "text-amber-600"}>
                {p.configured ? "live" : "sandbox"}
              </span>
            </Badge>
          ))}
        </div>
      </div>

      <div className="mt-4 flex flex-wrap items-end gap-2">
        <div className="w-40">
          <Label className="text-xs">Provider</Label>
          <Select value={provider} onValueChange={setProvider}>
            <SelectTrigger data-testid="collection-provider-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">Best available</SelectItem>
              {info.providers.map((p) => (
                <SelectItem key={p.name} value={p.name}>{p.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="w-52">
          <Label className="text-xs">Currency</Label>
          <Select value={ccy} onValueChange={setCcy}>
            <SelectTrigger data-testid="collection-currency-select">
              <SelectValue placeholder="Pick a currency" />
            </SelectTrigger>
            <SelectContent className="max-h-72">
              {options.map((c) => (
                <SelectItem key={c.currency} value={c.currency}
                  disabled={opened.has(`${c.currency}:${provider}`)}>
                  {c.currency} · {c.local ? c.rail : "SWIFT"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <Button onClick={create} disabled={busy || !ccy} data-testid="collection-create-btn">
          <Landmark className="h-4 w-4 me-1.5" />Get account details
        </Button>
      </div>

      {!!accounts.length && (
        <div className="mt-4 grid gap-3 sm:grid-cols-2" data-testid="collection-accounts-list">
          {accounts.map((a) => (
            <div key={a.id} className="rounded-xl border p-3.5" data-testid={`collection-account-${a.currency}`}>
              <div className="flex items-center justify-between gap-2">
                <div className="font-semibold text-sm">{a.currency} · {a.rail}</div>
                <Badge variant="secondary" className="text-[10px]">{a.provider_label}</Badge>
              </div>
              <div className="mt-2">
                <Detail label="Beneficiary" value={a.beneficiary} />
                <Detail label="IBAN" value={a.iban} />
                <Detail label="Account number" value={a.account_number} />
                <Detail label="Routing / sort code" value={a.routing_code} />
                <Detail label="SWIFT / BIC" value={a.swift} />
                <Detail label="Bank" value={a.bank_name} />
              </div>
              <div className="mt-2 flex items-center justify-between gap-2 text-[11px] text-muted-foreground">
                <span>Credited to your {a.credited_to} balance</span>
                <span className="flex items-center gap-1">
                  <Button variant="ghost" size="sm" className="h-7 gap-1 text-[11px]"
                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/collect/accounts/${a.id}/pdf`, "_blank")}
                    data-testid={`collection-pdf-${a.currency}`}>
                    <FileDown className="h-3 w-3" />Share details
                  </Button>
                  {a.mode !== "live" && (
                    <Button variant="ghost" size="sm" className="h-7 gap-1 text-[11px]"
                      onClick={() => simulate(a)} disabled={busy}
                      data-testid={`collection-simulate-${a.currency}`}>
                      <FlaskConical className="h-3 w-3" />Simulate payment
                    </Button>
                  )}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {!!accounts.some((a) => a.mode !== "live") && (
        <div className="mt-3 flex items-end gap-2">
          <div className="w-32">
            <Label className="text-xs">Sandbox amount</Label>
            <Input value={amount} onChange={(e) => setAmount(e.target.value)}
              className="h-9" data-testid="collection-sim-amount" />
          </div>
          <p className="text-[11px] text-muted-foreground pb-2">
            SANDBOX MODE — no provider keys yet, so account details are simulated and payments
            are triggered by you. Add keys and the same flow goes live.
          </p>
        </div>
      )}

      {!!payments.length && (
        <div className="mt-4" data-testid="collection-payments">
          <div className="flex items-center gap-2 text-xs font-medium mb-2">
            <ArrowDownToLine className="h-3.5 w-3.5" />Money in
            <span className="ms-auto flex items-center gap-1 text-[11px] font-normal text-muted-foreground"
              data-testid="collection-receipt-note">
              <Mail className="h-3 w-3" />
              Receipt emailed with the rate and fee
              {mail?.email_mode === "demo" && " (DEMO — recorded in your email log)"}
            </span>
          </div>
          <div className="divide-y">
            {payments.slice(0, 6).map((p) => (
              <div key={p.id} className="flex items-center justify-between py-2 text-xs">
                <div>
                  <div className="font-medium">{p.amount.toLocaleString()} {p.currency} · {p.sender}</div>
                  <div className="text-muted-foreground">{p.rail} · {p.provider}</div>
                </div>
                <div className="numeric font-medium text-emerald-600">
                  +{p.credited_amount.toLocaleString()} {p.credited_currency}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Card>
  );
};
